import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AppBar, Tag, Banner, Btn, cautionVariant } from "../components/index.js";
import { Card } from "../components/Card.js";
import { Frame } from "../components/Frame.js";
import { Highlighter } from "../components/Highlighter.js";
import { MarkdownText } from "../components/MarkdownText.js";
import { SwipeRow } from "../components/SwipeRow.js";
import { LabelingBlock, type LabelValue } from "../components/LabelingBlock.js";
import { BookPicker } from "../components/BookPicker.js";
import { LoadingState, ErrorState } from "../components/EmptyState.js";
import { useSurface } from "../lib/useSurface.js";
import { useAsync } from "../lib/useAsync.js";
import { manuals, books as booksApi, stores as storesApi, hasRole, mediaUrl, type UpdateStepInput } from "../lib/api.js";
import type { Book, Store, Step } from "@tebiki/shared";
import { bboxToFrame } from "../lib/bbox.js";
import { mmss } from "../lib/demo.js";
import { Icon, type IconName } from "../components/Icon.js";

interface EditAction {
  key: string;
  icon: IconName;
  label: string;
}

const EDIT_ACTIONS: EditAction[] = [
  { key: "speak", icon: "mic", label: "話して直す" },
  { key: "annotate", icon: "frame", label: "注釈" },
  { key: "text", icon: "pen", label: "テキスト編集" },
  { key: "caution", icon: "warning", label: "注意点" },
  { key: "table", icon: "table", label: "表" },
];

/** Screen E — finished preview / lightweight editor (console). */
export function Preview() {
  useSurface("console");
  const navigate = useNavigate();
  const location = useLocation();
  const { id = "" } = useParams();
  const { data, loading, error, offline, reload } = useAsync(() => manuals.get(id), [id]);
  const [busyInsert, setBusyInsert] = useState<string | null>(null); // afterStepId key ("null" = before first)
  const [busyDelete, setBusyDelete] = useState<string | null>(null); // stepId being deleted

  // ---- per-step image attach/replace (新規手順への画像追加・既存画像の差し替え) ----
  const [attachBusy, setAttachBusy] = useState<string | null>(null); // stepId being uploaded
  const attachInputRef = useRef<HTMLInputElement>(null);
  const attachTargetRef = useRef<{ stepId: string; t: number } | null>(null);

  function pickStepImage(stepId: string, timecodeMs: number | null) {
    attachTargetRef.current = { stepId, t: timecodeMs ?? 0 };
    attachInputRef.current?.click();
  }

  async function onStepImagePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    const target = attachTargetRef.current;
    attachTargetRef.current = null;
    if (!file || !target) return;
    setAttachBusy(target.stepId);
    try {
      await manuals.uploadKeyframe(id, target.stepId, file, target.t);
      await reload();
    } catch {
      alert("画像の保存に失敗しました。通信環境を確認してください。");
    } finally {
      setAttachBusy(null);
    }
  }

  // ---- label editor (store × category × tags) on the cover ----
  const canLabel = hasRole("owner", "admin", "editor");
  const [editingLabels, setEditingLabels] = useState(false);
  const [labels, setLabels] = useState<LabelValue | null>(null);
  const [savingLabels, setSavingLabels] = useState(false);
  const [storeList, setStoreList] = useState<Store[]>([]);
  const [catSuggest, setCatSuggest] = useState<string[]>([]);
  useEffect(() => {
    if (data && labels === null) {
      setLabels({ storeId: data.meta.storeId ?? null, category: data.meta.category ?? "", tags: data.meta.tags ?? [] });
    }
  }, [data, labels]);
  useEffect(() => {
    if (!canLabel) return;
    void storesApi.list().then(setStoreList).catch(() => setStoreList([]));
    void manuals
      .list()
      .then((r) => setCatSuggest([...new Set(r.items.map((m) => m.meta?.category).filter((c): c is string => !!c))]))
      .catch(() => setCatSuggest([]));
  }, [canLabel]);
  const storeName = (id: string | null) =>
    id ? storeList.find((s) => s.id === id)?.name ?? "店舗" : "店舗未設定";

  // ---- 本 (books) — tag-like attach targets for the label editor + the submit sheet ----
  const [bookList, setBookList] = useState<Book[]>([]);
  useEffect(() => {
    if (!canLabel) return;
    void booksApi.list().then(setBookList).catch(() => setBookList([]));
  }, [canLabel]);

  // ---- 提出シート (submit sheet): title confirm → 本 → 店舗 → 必読 → 承認へ / すぐ公開 ----
  const canPublish = hasRole("owner", "admin", "reviewer");
  const isPublished = data?.status === "published";
  const [sheetOpen, setSheetOpen] = useState(false);
  const [submitting, setSubmitting] = useState<"review" | "publish" | "save" | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [titleDraft, setTitleDraft] = useState("");
  const [storeDraft, setStoreDraft] = useState<string | null>(null);
  const [mustDraft, setMustDraft] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const sheetTitleRef = useRef<HTMLInputElement>(null);
  const submitBtnRef = useRef<HTMLButtonElement>(null);

  function openSheet() {
    if (!data) return;
    setTitleDraft(data.title);
    setStoreDraft(labels?.storeId ?? data.meta.storeId ?? null);
    const inMustBook = bookList.some((b) => b.manualIds.includes(id) && b.mustRead);
    setMustDraft(inMustBook || (labels?.tags ?? data.meta.tags ?? []).includes("必読"));
    setSubmitError(null);
    setSheetOpen(true);
  }
  useEffect(() => {
    if (sheetOpen) sheetTitleRef.current?.focus();
    else submitBtnRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetOpen]);

  /** Keep keyboard focus inside the sheet; Esc backs out (unless a submit is in flight). */
  function onSheetKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape" && !submitting) {
      e.preventDefault();
      setSheetOpen(false);
      return;
    }
    if (e.key !== "Tab") return;
    const root = sheetRef.current;
    if (!root) return;
    const focusable = Array.from(
      root.querySelectorAll<HTMLElement>(
        "button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href]",
      ),
    );
    if (focusable.length === 0) return;
    const firstEl = focusable[0];
    const lastEl = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && active === firstEl) {
      e.preventDefault();
      lastEl.focus();
    } else if (!e.shiftKey && active === lastEl) {
      e.preventDefault();
      firstEl.focus();
    }
  }

  async function submit(mode: "review" | "publish" | "save") {
    if (!data || submitting) return;
    setSubmitting(mode);
    setSubmitError(null);
    try {
      const tags = new Set(labels?.tags ?? data.meta.tags ?? []);
      if (mustDraft) tags.add("必読");
      else tags.delete("必読");
      const finalTitle = titleDraft.trim() || data.title;
      await manuals.update(id, { title: finalTitle, storeId: storeDraft, tags: [...tags] });
      // 必読は本単位の概念: ONなら、この手順書を収めた本を必読に昇格させる（他の手順書も
      // 抱える本を勝手にOFFへ落とすことはしない）。
      if (mustDraft) {
        const targets = bookList.filter((b) => b.manualIds.includes(id) && !b.mustRead);
        await Promise.allSettled(targets.map((b) => booksApi.update(b.bookId, { mustRead: true })));
      }
      // 公開中のマニュアルでは status に触れない（黙って公開を取り下げない）。
      if (mode === "publish") await manuals.approve(id);
      else if (mode === "review") await manuals.update(id, { status: "review" });
      navigate("/library", { state: { submitted: { mode, title: finalTitle } } });
    } catch {
      setSubmitError(
        mode === "publish"
          ? "公開に失敗しました。権限と通信環境を確認してください。"
          : mode === "save"
            ? "保存に失敗しました。通信環境を確認してください。"
            : "提出に失敗しました。通信環境を確認してください。",
      );
      setSubmitting(null);
    }
  }

  async function saveLabels() {
    if (!labels) return;
    setSavingLabels(true);
    try {
      await manuals.update(id, { storeId: labels.storeId, category: labels.category.trim() || null, tags: labels.tags });
      setEditingLabels(false);
      await reload();
    } catch {
      alert("ラベルの保存に失敗しました。通信環境を確認してください。");
    } finally {
      setSavingLabels(false);
    }
  }

  async function handleInsert(afterStepId: string | null) {
    const key = afterStepId ?? "null";
    if (busyInsert) return;
    setBusyInsert(key);
    try {
      await manuals.insertStep(id, afterStepId);
      await reload();
    } catch {
      // silently ignore — API might not be connected in demo mode
    } finally {
      setBusyInsert(null);
    }
  }

  async function handleDelete(stepId: string) {
    // No extra confirm: the left-swipe to reveal + tap on 削除 is already a deliberate two-step gesture.
    if (busyDelete) return;
    setBusyDelete(stepId);
    try {
      await manuals.deleteStep(id, stepId);
      await reload();
    } catch {
      // silently ignore
    } finally {
      setBusyDelete(null);
    }
  }

  // ---- per-step editors (話して直す / テキスト編集 / 注意点 / 表) ----
  type EditorKind = "speak" | "text" | "caution" | "table";
  type CLevel = "info" | "warning" | "danger";
  const [editor, setEditor] = useState<{ stepId: string; kind: EditorKind } | null>(null);
  const [savingStep, setSavingStep] = useState(false);
  const [editErr, setEditErr] = useState<string | null>(null);
  // text
  const [txTitle, setTxTitle] = useState("");
  const [txCaption, setTxCaption] = useState("");
  // cautions
  const [cauDraft, setCauDraft] = useState<{ level: CLevel; text: string }[]>([]);
  // table
  const [tblDraft, setTblDraft] = useState<{ columns: string[]; rows: string[][] } | null>(null);
  // speak (話して直す)
  const [recState, setRecState] = useState<"idle" | "recording" | "sending">("idle");
  const [speakMsg, setSpeakMsg] = useState<string | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const recChunksRef = useRef<Blob[]>([]);
  const recStreamRef = useRef<MediaStream | null>(null);
  const speakTargetRef = useRef<string | null>(null);

  function openEditor(step: Step, kind: EditorKind) {
    if (editor?.stepId === step.stepId && editor.kind === kind) {
      closeEditor();
      return;
    }
    stopRecStream();
    setRecState("idle");
    setEditErr(null);
    setSpeakMsg(null);
    if (kind === "text") {
      setTxTitle(step.title ?? "");
      setTxCaption(step.caption ?? "");
    } else if (kind === "caution") {
      setCauDraft((step.cautions ?? []).map((c) => ({ level: c.level, text: c.text })));
    } else if (kind === "table") {
      setTblDraft(
        step.table ? { columns: [...step.table.columns], rows: step.table.rows.map((r) => [...r]) } : null,
      );
    }
    setEditor({ stepId: step.stepId, kind });
  }

  function closeEditor() {
    stopRecStream();
    setRecState("idle");
    setEditor(null);
  }

  async function saveStepPatch(stepId: string, body: UpdateStepInput) {
    setSavingStep(true);
    setEditErr(null);
    try {
      await manuals.updateStep(id, stepId, body);
      await reload();
      setEditor(null);
    } catch {
      setEditErr("保存に失敗しました。通信環境を確認してください。");
    } finally {
      setSavingStep(false);
    }
  }

  // --- 話して直す recording ---
  function stopRecStream() {
    const rec = recRef.current;
    if (rec && rec.state !== "inactive") {
      rec.onstop = null;
      try {
        rec.stop();
      } catch {
        /* already stopping */
      }
    }
    recRef.current = null;
    recStreamRef.current?.getTracks().forEach((t) => t.stop());
    recStreamRef.current = null;
  }
  useEffect(() => () => stopRecStream(), []); // eslint-disable-line react-hooks/exhaustive-deps

  async function startSpeakRec(stepId: string) {
    setEditErr(null);
    setSpeakMsg(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recStreamRef.current = stream;
      recChunksRef.current = [];
      speakTargetRef.current = stepId;
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find(
        (t) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(t),
      );
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      rec.ondataavailable = (e) => e.data.size && recChunksRef.current.push(e.data);
      rec.onstop = () => void sendSpeakFix();
      rec.start(500);
      recRef.current = rec;
      setRecState("recording");
    } catch {
      setEditErr("マイクを利用できません。ブラウザの権限を確認してください。");
    }
  }

  function finishSpeakRec() {
    const rec = recRef.current;
    if (!rec || rec.state === "inactive") return;
    setRecState("sending");
    try {
      rec.stop(); // → onstop → sendSpeakFix
    } catch {
      setRecState("idle");
    }
  }

  async function sendSpeakFix() {
    const stepId = speakTargetRef.current;
    const type = recChunksRef.current[0]?.type || "audio/webm";
    const blob = new Blob(recChunksRef.current, { type });
    recChunksRef.current = [];
    recRef.current = null;
    recStreamRef.current?.getTracks().forEach((t) => t.stop());
    recStreamRef.current = null;
    if (!stepId || blob.size === 0) {
      setRecState("idle");
      return;
    }
    try {
      const res = await manuals.speakFix(id, stepId, blob);
      setSpeakMsg(res.changeSummary || "変更を適用しました");
      await reload();
    } catch {
      setEditErr("音声の適用に失敗しました。もう一度お試しください。");
    } finally {
      setRecState("idle");
    }
  }

  // 戻り先: 来た場所（state.from）を最優先。無ければ「動画×下書き」だけがクリップ確認(Picker)へ、
  // それ以外（画像フロー・提出済み・公開中・承認画面や書架から開いた場合）は書架へ。
  const backTo =
    ((location.state as { from?: string } | null)?.from) ??
    (id && data?.sourceType === "video" && data?.status === "draft" ? `/picker/${id}` : "/library");

  return (
    <div className="shell">
      <AppBar
        back={backTo}
        backLabel={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Icon name="chevron" size={16} style={{ transform: "rotate(90deg)" }} /> 戻る
          </span>
        }
        compactBrand
      >
        <Tag dot color="var(--ok)">
          AI品質 92
        </Tag>
        <span className="statuspill">
          {data?.status === "review" ? "承認待ち" : data?.status === "published" ? "公開中" : "下書き · 自動保存"}
        </span>
      </AppBar>

      <input ref={attachInputRef} type="file" accept="image/*" hidden onChange={(e) => void onStepImagePicked(e)} />
      <main className="doc" style={{ maxWidth: 820, margin: "0 auto", padding: "30px 24px 130px" }}>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} offline={offline} onRetry={reload} />
        ) : data ? (
          <>
            <Banner variant="ai" className="reveal">
              <span className="jp grow">
                <b>AIレビュー:</b> 手順2に安全上の注意を追加することを提案します（刃物を使う場合）。
              </span>
              <button className="ebtn" style={ebtn} onClick={() => navigate(`/ai-review?id=${id}`)}>
                提案を見る
              </button>
            </Banner>

            <Card crop className="cover reveal d1" style={{ padding: 30, marginBottom: 22, marginTop: 18 }}>
              <div className="kicker">MANUAL · v{data.version} draft</div>
              <h1 className="jp display" style={{ margin: "10px 0 6px", fontSize: "clamp(30px,5vw,50px)" }}>
                {data.title}
              </h1>
              <div className="row" style={{ flexWrap: "wrap", marginTop: 8, gap: 8, alignItems: "center" }}>
                <Tag mono>{data.steps.length} STEPS</Tag>
                <Tag>{storeName(data.meta.storeId ?? null)}</Tag>
                <Tag>{data.meta.category ?? "未分類"}</Tag>
                <Tag>所要 {data.meta.estimatedTotalMinutes ?? 1}分</Tag>
                {data.meta.tags?.includes("必読") ? <Tag hi>必読</Tag> : null}
                {data.titleSource === "ai_generated" ? (
                  <Tag dot color="var(--info)">
                    AI生成タイトル
                  </Tag>
                ) : null}
                {canLabel ? (
                  <button
                    type="button"
                    className="ebtn"
                    style={{ ...ebtnRow, marginLeft: "auto" }}
                    onClick={() => setEditingLabels((v) => !v)}
                    aria-expanded={editingLabels}
                  >
                    <Icon name="edit" size={14} /> ラベル編集
                  </button>
                ) : null}
              </div>
              {canLabel && editingLabels && labels ? (
                <div style={{ marginTop: 16, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
                  <LabelingBlock value={labels} onChange={setLabels} stores={storeList} categorySuggestions={catSuggest} compact />
                  <div style={{ marginTop: 14 }}>
                    <span className="field-label" style={{ display: "block", marginBottom: 6 }}>本（この手順書を収める）</span>
                    <BookPicker manualId={id} books={bookList} onBooksChange={setBookList} />
                  </div>
                  <div className="row" style={{ gap: 10, marginTop: 14 }}>
                    <Btn variant="primary" onClick={saveLabels} disabled={savingLabels}>
                      {savingLabels ? "保存中…" : "ラベルを保存"}
                    </Btn>
                    <Btn
                      variant="ghost"
                      onClick={() => {
                        setEditingLabels(false);
                        setLabels({ storeId: data.meta.storeId ?? null, category: data.meta.category ?? "", tags: data.meta.tags ?? [] });
                      }}
                    >
                      キャンセル
                    </Btn>
                  </div>
                </div>
              ) : null}
            </Card>

            <Card className="reveal d2" style={{ overflow: "visible" }}>
              {/* Insertion divider before the first step (always-visible "+" on the right) */}
              <InsertDivider
                afterStepId={null}
                line={false}
                busy={busyInsert === "null"}
                onInsert={handleInsert}
              />

              {data.steps.map((step, i) => (
                <div key={step.stepId}>
                  <SwipeRow
                    onDelete={() => handleDelete(step.stepId)}
                    deleting={busyDelete === step.stepId}
                    disabled={data.steps.length <= 1}
                  >
                  <article
                    className="sline"
                    style={{
                      position: "relative",
                      display: "grid",
                      gridTemplateColumns: "auto 1fr",
                      gap: 22,
                      padding: 22,
                    }}
                  >
                    <div className="n" style={nStyle}>
                      {String(step.order).padStart(2, "0")}
                    </div>
                    <div>
                      {!step.imageUrl && canLabel ? (
                        /* 画像のない手順（例: 追加したての手順）— その場で写真を選んで/撮って添付 */
                        <button
                          type="button"
                          style={picAddStyle}
                          onClick={() => pickStepImage(step.stepId, step.videoTimecodeMs ?? null)}
                          disabled={attachBusy === step.stepId}
                        >
                          <Icon name="plus" size={18} />
                          {attachBusy === step.stepId ? "追加中…" : "画像を追加"}
                        </button>
                      ) : (
                        <Frame
                          paper
                          src={mediaUrl(step.imageUrl)}
                          cap={step.videoTimecodeMs != null ? mmss(step.videoTimecodeMs) : undefined}
                          bbox={bboxToFrame(step.bbox, step.objectLabel)}
                          className="pic"
                          style={{ width: 140, aspectRatio: "3/4", float: "left", margin: "0 18px 8px 0" }}
                        >
                          {step.highlights?.strokes?.length ? (
                            <Highlighter
                              src={null}
                              value={step.highlights.strokes}
                              readOnly
                              style={{ position: "absolute", inset: 0 }}
                            />
                          ) : null}
                        </Frame>
                      )}
                      <div className="spread">
                        <h2 className="t-lg jp">{step.title}</h2>
                        {step.videoTimecodeMs != null ? <Tag mono>{mmss(step.videoTimecodeMs)}</Tag> : null}
                      </div>
                      <MarkdownText
                        className="jp"
                        style={{ margin: "8px 0 0" }}
                        text={step.caption}
                        terms={step.highlights?.terms}
                      />
                      {step.cautions.map((c, ci) => (
                        <Banner key={ci} variant={cautionVariant(c.level)}>
                          <span>{c.text}</span>
                        </Banner>
                      ))}
                      {step.table && step.table.columns.length ? (
                        /* many-column tables can exceed a phone column — scroll INSIDE this box,
                           never the page (scrollbar only appears when needed) */
                        <div style={{ marginTop: 16, clear: "both", overflowX: "auto" }}>
                          <table className="tbl">
                            <thead>
                              <tr>
                                {step.table.columns.map((col, ci) => (
                                  <th key={ci}>{col}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {step.table.rows.map((r, ri) => (
                                <tr key={ri}>
                                  {r.map((cell, ci) => (
                                    <td key={ci}>{cell}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                      <div className="edit-row" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                        {canLabel && step.imageUrl ? (
                          <button
                            className="ebtn"
                            style={ebtnRow}
                            disabled={attachBusy === step.stepId}
                            onClick={() => pickStepImage(step.stepId, step.videoTimecodeMs ?? null)}
                          >
                            <Icon name="capture" size={16} />
                            {attachBusy === step.stepId ? "差し替え中…" : "画像を差し替え"}
                          </button>
                        ) : null}
                        {EDIT_ACTIONS.map((a) => {
                          const isAnnotate = a.key === "annotate";
                          const enabled = isAnnotate || canLabel;
                          const open = editor?.stepId === step.stepId && editor.kind === a.key;
                          return (
                            <button
                              key={a.key}
                              className="ebtn"
                              style={{
                                ...ebtnRow,
                                ...(open
                                  ? { borderColor: "var(--hi-deep)", background: "color-mix(in srgb,var(--hi) 16%,var(--surface))" }
                                  : null),
                              }}
                              disabled={!enabled}
                              title={enabled ? undefined : "編集権限が必要です"}
                              aria-disabled={enabled ? undefined : true}
                              aria-expanded={isAnnotate ? undefined : open}
                              onClick={() => {
                                if (isAnnotate) navigate(`/annotate/${id}?step=${step.stepId}`);
                                else openEditor(step, a.key as "speak" | "text" | "caution" | "table");
                              }}
                            >
                              <Icon name={a.icon} size={16} />
                              {a.label}
                            </button>
                          );
                        })}
                      </div>
                      {editor?.stepId === step.stepId ? (
                        <div style={editorPanelStyle}>
                          {editor.kind === "text" ? (
                            <div style={{ display: "grid", gap: 10 }}>
                              <div>
                                <label className="field-label" htmlFor={`tx-title-${step.stepId}`}>見出し</label>
                                <input
                                  id={`tx-title-${step.stepId}`}
                                  className="input jp"
                                  value={txTitle}
                                  onChange={(e) => setTxTitle(e.target.value)}
                                  placeholder="手順の見出し（〜する）"
                                />
                              </div>
                              <div>
                                <label className="field-label" htmlFor={`tx-cap-${step.stepId}`}>説明</label>
                                <textarea
                                  id={`tx-cap-${step.stepId}`}
                                  className="area jp"
                                  rows={4}
                                  value={txCaption}
                                  onChange={(e) => setTxCaption(e.target.value)}
                                  placeholder="現場の人がそのまま実行できる説明"
                                  style={{ resize: "vertical", lineHeight: 1.7 }}
                                />
                              </div>
                              <div className="row" style={{ gap: 8 }}>
                                <Btn variant="primary" onClick={() => void saveStepPatch(step.stepId, { heading: txTitle.trim(), caption: txCaption })} disabled={savingStep || !txTitle.trim()}>
                                  {savingStep ? "保存中…" : "保存"}
                                </Btn>
                                <Btn variant="ghost" onClick={closeEditor} disabled={savingStep}>キャンセル</Btn>
                              </div>
                            </div>
                          ) : editor.kind === "caution" ? (
                            <div style={{ display: "grid", gap: 10 }}>
                              <span className="field-label">注意点（info=情報 / warning=注意 / danger=危険）</span>
                              {cauDraft.length === 0 ? (
                                <p className="jp" style={{ margin: 0, fontSize: 13, color: "var(--muted)" }}>注意点はまだありません。「追加」から作成できます。</p>
                              ) : null}
                              {cauDraft.map((cd, ci) => (
                                <div key={ci} className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                  <select
                                    className="select"
                                    style={{ width: 130 }}
                                    aria-label={`注意点${ci + 1}のレベル`}
                                    value={cd.level}
                                    onChange={(e) => setCauDraft((prev) => prev.map((x, i) => (i === ci ? { ...x, level: e.target.value as "info" | "warning" | "danger" } : x)))}
                                  >
                                    <option value="info">info</option>
                                    <option value="warning">warning</option>
                                    <option value="danger">danger</option>
                                  </select>
                                  <input
                                    className="input jp"
                                    style={{ flex: "1 1 220px" }}
                                    aria-label={`注意点${ci + 1}の内容`}
                                    value={cd.text}
                                    onChange={(e) => setCauDraft((prev) => prev.map((x, i) => (i === ci ? { ...x, text: e.target.value } : x)))}
                                    placeholder="注意の内容"
                                  />
                                  <button className="ebtn" style={ebtnRow} onClick={() => setCauDraft((prev) => prev.filter((_, i) => i !== ci))} aria-label={`注意点${ci + 1}を削除`}>
                                    <Icon name="close" size={14} /> 削除
                                  </button>
                                </div>
                              ))}
                              <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                                <button className="ebtn" style={ebtnRow} onClick={() => setCauDraft((prev) => [...prev, { level: "warning", text: "" }])} disabled={cauDraft.length >= 10}>
                                  <Icon name="plus" size={14} /> 追加
                                </button>
                                <span className="grow" />
                                <Btn variant="primary" onClick={() => void saveStepPatch(step.stepId, { cautions: cauDraft.filter((x) => x.text.trim()).map((x) => ({ level: x.level, text: x.text.trim() })) })} disabled={savingStep}>
                                  {savingStep ? "保存中…" : "保存"}
                                </Btn>
                                <Btn variant="ghost" onClick={closeEditor} disabled={savingStep}>キャンセル</Btn>
                              </div>
                            </div>
                          ) : editor.kind === "table" ? (
                            <div style={{ display: "grid", gap: 10 }}>
                              {tblDraft ? (
                                <>
                                  <span className="field-label">表（分量・設定値など）</span>
                                  <div style={{ display: "grid", gap: 8 }}>
                                    <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                                      {tblDraft.columns.map((col, ci) => (
                                        <input
                                          key={ci}
                                          className="input"
                                          style={{ flex: "1 1 120px", fontWeight: 600 }}
                                          aria-label={`列${ci + 1}の見出し`}
                                          value={col}
                                          onChange={(e) => setTblDraft((prev) => (prev ? { ...prev, columns: prev.columns.map((x, i) => (i === ci ? e.target.value : x)) } : prev))}
                                          placeholder={`列${ci + 1}`}
                                        />
                                      ))}
                                    </div>
                                    {tblDraft.rows.map((r, ri) => (
                                      <div key={ri} className="row" style={{ gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                                        {r.map((cell, ci) => (
                                          <input
                                            key={ci}
                                            className="input jp"
                                            style={{ flex: "1 1 120px" }}
                                            aria-label={`行${ri + 1} 列${ci + 1}`}
                                            value={cell}
                                            onChange={(e) => setTblDraft((prev) => (prev ? { ...prev, rows: prev.rows.map((row, i) => (i === ri ? row.map((x, j) => (j === ci ? e.target.value : x)) : row)) } : prev))}
                                          />
                                        ))}
                                        <button className="ebtn" style={ebtnRow} onClick={() => setTblDraft((prev) => (prev ? { ...prev, rows: prev.rows.filter((_, i) => i !== ri) } : prev))} aria-label={`行${ri + 1}を削除`}>
                                          <Icon name="close" size={14} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                                    <button className="ebtn" style={ebtnRow} onClick={() => setTblDraft((prev) => (prev ? { ...prev, rows: [...prev.rows, prev.columns.map(() => "")] } : prev))} disabled={tblDraft.rows.length >= 50}>
                                      <Icon name="plus" size={14} /> 行を追加
                                    </button>
                                    <button className="ebtn" style={ebtnRow} onClick={() => setTblDraft((prev) => (prev ? { columns: [...prev.columns, ""], rows: prev.rows.map((r) => [...r, ""]) } : prev))} disabled={tblDraft.columns.length >= 12}>
                                      <Icon name="plus" size={14} /> 列を追加
                                    </button>
                                    <button
                                      className="ebtn"
                                      style={{ ...ebtnRow, color: "var(--danger)" }}
                                      onClick={() => setTblDraft((prev) => (prev && prev.columns.length > 1 ? { columns: prev.columns.slice(0, -1), rows: prev.rows.map((r) => r.slice(0, -1)) } : prev))}
                                      disabled={tblDraft.columns.length <= 1}
                                    >
                                      <Icon name="close" size={14} /> 最後の列を削除
                                    </button>
                                    <span className="grow" />
                                    <button className="ebtn" style={{ ...ebtnRow, color: "var(--danger)" }} onClick={() => void saveStepPatch(step.stepId, { table: null })} disabled={savingStep}>
                                      表を削除
                                    </button>
                                    <Btn
                                      variant="primary"
                                      onClick={() => {
                                        const cols = tblDraft.columns.map((c2) => c2.trim());
                                        void saveStepPatch(step.stepId, { table: { columns: cols, rows: tblDraft.rows.map((r) => r.map((x) => x.trim())) } });
                                      }}
                                      disabled={savingStep || tblDraft.columns.every((c2) => !c2.trim())}
                                    >
                                      {savingStep ? "保存中…" : "保存"}
                                    </Btn>
                                    <Btn variant="ghost" onClick={closeEditor} disabled={savingStep}>キャンセル</Btn>
                                  </div>
                                </>
                              ) : (
                                <div className="row" style={{ gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                                  <p className="jp" style={{ margin: 0, fontSize: 13, color: "var(--muted)" }}>この手順には表がありません。分量・設定値などの一覧を追加できます。</p>
                                  <Btn variant="primary" icon={<Icon name="table" size={15} />} onClick={() => setTblDraft({ columns: ["項目", "値"], rows: [["", ""], ["", ""]] })}>
                                    表を作成
                                  </Btn>
                                  <Btn variant="ghost" onClick={closeEditor}>キャンセル</Btn>
                                </div>
                              )}
                            </div>
                          ) : (
                            /* ---- 話して直す ---- */
                            <div style={{ display: "grid", gap: 10 }}>
                              <p className="jp" style={{ margin: 0, fontSize: 13.5, lineHeight: 1.7 }}>
                                直したい内容を<b>声で</b>指示してください。例:「茹で時間を45秒に直して」「刃物に注意という危険レベルの注意点を追加して」
                              </p>
                              <div className="row" style={{ gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                                {recState === "idle" ? (
                                  <Btn variant="primary" icon={<Icon name="mic" size={15} />} onClick={() => void startSpeakRec(step.stepId)}>
                                    録音を開始
                                  </Btn>
                                ) : recState === "recording" ? (
                                  <Btn variant="hi" icon={<Icon name="check" size={15} />} onClick={finishSpeakRec}>
                                    <span className="rec-dot" aria-hidden="true" style={{ marginRight: 6 }} />
                                    録音を終了して適用
                                  </Btn>
                                ) : (
                                  <Btn variant="primary" disabled icon={<Icon name="spinner" size={15} className="ic-spin" />}>
                                    AIが適用中…
                                  </Btn>
                                )}
                                <Btn variant="ghost" onClick={closeEditor} disabled={recState === "sending"}>閉じる</Btn>
                                {recState === "recording" ? (
                                  <span className="mono" role="status" style={{ fontSize: 12, color: "var(--hi-deep)" }}>録音中 — 話し終えたら「終了して適用」</span>
                                ) : null}
                              </div>
                              {speakMsg ? (
                                <p className="jp" role="status" style={{ margin: 0, fontSize: 13, color: "var(--ok)" }}>
                                  <Icon name="check" size={14} /> {speakMsg}
                                </p>
                              ) : null}
                            </div>
                          )}
                          {editErr ? (
                            <p className="jp" role="alert" style={{ margin: "10px 0 0", fontSize: 13, color: "var(--danger)" }}>
                              {editErr}
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </article>
                  </SwipeRow>

                  {/* Insertion divider after each step (dashed line between steps + "+" on the right) */}
                  <InsertDivider
                    afterStepId={step.stepId}
                    line={i < data.steps.length - 1}
                    busy={busyInsert === step.stepId}
                    onInsert={handleInsert}
                  />
                </div>
              ))}
            </Card>

            <div className="row reveal d3" style={{ justifyContent: "center", marginTop: 18, flexWrap: "wrap" }}>
              <button className="ebtn" style={ebtnRow} onClick={() => handleInsert(data.steps[data.steps.length - 1]?.stepId ?? null)}>
                <Icon name="plus" size={16} /> 手順を追加
              </button>
              <button className="ebtn" style={ebtnRow} disabled title="準備中" aria-disabled>
                <Icon name="reorder" size={16} /> 並べ替え
              </button>
              <button className="ebtn" style={ebtnRow} onClick={() => navigate(`/multilingual?id=${id}`)}>
                <Icon name="multilingual" size={16} /> 多言語に展開
              </button>
              <button className="ebtn" style={ebtnRow} onClick={() => navigate(`/digest/${id}`)}>
                <Icon name="ai-review" size={16} /> 重要ポイントだけ
              </button>
              <button className="ebtn" style={ebtnRow} onClick={() => navigate(`/checklist/${id}`)}>
                <Icon name="approval" size={16} /> チェックリスト
              </button>
              <button className="ebtn" style={ebtnRow} onClick={() => navigate(`/ai-review?id=${id}`)}>
                <Icon name="ai-review" size={16} /> AIレビュー
              </button>
            </div>
          </>
        ) : null}
      </main>

      <div className="actionbar">
        <Btn variant="ghost" lg disabled title="自動保存" aria-disabled>
          下書き保存
        </Btn>
        <span className="grow" />
        {canLabel ? (
          <button ref={submitBtnRef} className="btn btn--primary btn--lg" onClick={openSheet} disabled={!data}>
            <span className="ico" aria-hidden="true"><Icon name="target" /></span>
            {isPublished ? "本・ラベルを変更" : data?.status === "review" ? "提出内容を変更" : "提出する"}
          </button>
        ) : null}
      </div>

      {/* ---- 提出シート: 確認して、本に収めて、届ける ---- */}
      {sheetOpen && data ? (
        <div
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !submitting) setSheetOpen(false);
          }}
          style={{ position: "fixed", inset: 0, zIndex: 40, background: "rgba(18,22,27,.44)", display: "grid", alignItems: "end", justifyItems: "center" }}
        >
          <div
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            aria-label="マニュアルを提出"
            aria-busy={!!submitting}
            onKeyDown={onSheetKeyDown}
            className="reveal"
            style={{
              width: "min(680px, 100%)",
              maxHeight: "88dvh",
              overflowY: "auto",
              background: "var(--surface)",
              borderRadius: "16px 16px 0 0",
              border: "1px solid var(--line)",
              borderBottom: 0,
              boxShadow: "var(--shadow-3)",
              padding: "22px 24px calc(20px + env(safe-area-inset-bottom))",
            }}
          >
            <div className="kicker">撮る · 話す · <span style={{ color: "var(--hi-deep)" }}>届ける</span></div>
            <h2 className="display jp" style={{ margin: "8px 0 4px", fontSize: "clamp(22px,4vw,30px)" }}>
              {isPublished ? "本・ラベルを変更" : "この内容で提出しますか？"}
            </h2>
            <p className="jp" style={{ margin: "0 0 18px", fontSize: 13.5, color: "var(--muted)", lineHeight: 1.7 }}>
              {isPublished
                ? "このマニュアルは公開中です。ここでの変更は公開状態のまま反映されます。"
                : "本に収めると、対象スタッフの書架に自動で並びます。提出後は承認者の確認を経て公開されます。"}
            </p>

            <div style={{ display: "grid", gap: 16 }}>
              <div>
                <label className="field-label" htmlFor="submit-title">タイトル</label>
                <input
                  id="submit-title"
                  ref={sheetTitleRef}
                  className="input jp"
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  placeholder="マニュアルのタイトル"
                />
              </div>

              <div>
                <span className="field-label" style={{ display: "block", marginBottom: 6 }}>本（この手順書を収める）</span>
                <BookPicker manualId={id} books={bookList} onBooksChange={setBookList} disabled={!!submitting} />
              </div>

              <div className="row" style={{ gap: 12, flexWrap: "wrap", alignItems: "end" }}>
                <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                  <label className="field-label" htmlFor="submit-store">店舗</label>
                  <select
                    id="submit-store"
                    className="select"
                    value={storeDraft ?? ""}
                    onChange={(e) => setStoreDraft(e.target.value || null)}
                  >
                    <option value="">店舗を選択…</option>
                    {storeList.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  aria-pressed={mustDraft}
                  onClick={() => setMustDraft((v) => !v)}
                  className="tag"
                  title="必読は本単位です。ONにすると、この手順書を収めた本が必読になります"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    minHeight: 44,
                    padding: "10px 14px",
                    cursor: "pointer",
                    borderColor: mustDraft ? "var(--hi-deep)" : "var(--line-2)",
                    color: mustDraft ? "var(--ink)" : "var(--muted)",
                    background: mustDraft ? "color-mix(in srgb,var(--hi) 16%,transparent)" : "transparent",
                  }}
                >
                  <Icon name={mustDraft ? "check" : "star"} size={14} /> 収めた本を必読にする（クイズ80%で修了）
                </button>
              </div>

              {submitError ? (
                <p className="jp" role="alert" style={{ margin: 0, fontSize: 13, color: "var(--danger)" }}>
                  {submitError}
                </p>
              ) : null}

              <div className="row" style={{ gap: 10, flexWrap: "wrap", marginTop: 4 }}>
                <Btn variant="ghost" lg onClick={() => setSheetOpen(false)} disabled={!!submitting}>
                  キャンセル
                </Btn>
                <span className="grow" />
                {isPublished ? (
                  /* 公開中: statusには触れず本・ラベルだけ保存（黙って公開を取り下げない） */
                  <Btn variant="primary" lg icon={<Icon name="check" />} onClick={() => void submit("save")} disabled={!!submitting}>
                    {submitting === "save" ? "保存中…" : "変更を保存"}
                  </Btn>
                ) : (
                  <>
                    <Btn variant="primary" lg icon={<Icon name="approval" />} onClick={() => void submit("review")} disabled={!!submitting}>
                      {submitting === "review" ? "提出中…" : "承認に出す"}
                    </Btn>
                    {canPublish ? (
                      <Btn variant="hi" lg icon={<Icon name="check" />} onClick={() => void submit("publish")} disabled={!!submitting}>
                        {submitting === "publish" ? "公開中…" : "すぐ公開"}
                      </Btn>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <style>{insertZoneCSS}</style>
    </div>
  );
}

// ---------- InsertDivider component ----------

interface InsertDividerProps {
  afterStepId: string | null;
  /** draw the dashed divider line (between steps; omit before the first / after the last) */
  line: boolean;
  busy: boolean;
  onInsert: (afterStepId: string | null) => void;
}

/** A break line with an always-visible circular "+" sticking out on the right (touch-friendly —
 *  no hover needed). Pressing it inserts a new step at this position. */
function InsertDivider({ afterStepId, line, busy, onInsert }: InsertDividerProps) {
  return (
    <div className="ins-div">
      {line ? <span className="ins-line" aria-hidden="true" /> : null}
      <button
        type="button"
        className="ins-plus"
        aria-label="ここに手順を追加"
        disabled={busy}
        onClick={() => onInsert(afterStepId)}
      >
        {busy ? (
          <span className="ins-dot" aria-hidden="true" />
        ) : (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 3v10M3 8h10" stroke="#10130a" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
        )}
      </button>
    </div>
  );
}

const insertZoneCSS = `
  .ins-div { position: relative; height: 20px; }
  .ins-line { position: absolute; left: 0; right: 0; top: 50%; border-top: 1px dashed var(--line); }
  .ins-plus {
    position: absolute; right: -15px; top: 50%; transform: translateY(-50%);
    width: 34px; height: 34px; border-radius: 50%; border: 0;
    background: var(--hi, #c6f135); color: #10130a;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; z-index: 3;
    box-shadow: 0 5px 14px -3px rgba(0,0,0,.32);
    transition: transform .12s ease, box-shadow .12s ease;
  }
  .ins-plus:hover { transform: translateY(-50%) scale(1.09); box-shadow: 0 7px 18px -4px rgba(0,0,0,.4); }
  .ins-plus:active { transform: translateY(-50%) scale(.95); }
  /* ink outline (3:1+ on paper) with the lime as a halo — lime alone is ~1.2:1 on white */
  .ins-plus:focus-visible { outline: 2px solid var(--ink); outline-offset: 3px; box-shadow: 0 0 0 6px color-mix(in srgb, var(--hi, #c6f135) 60%, transparent); }
  .ins-plus:disabled { opacity: .6; cursor: wait; }
  /* touch: 44px hit target. Must live in THIS sheet — tebiki.css's copy of this rule
     loses to the base .ins-plus above by document order. */
  @media (pointer: coarse) { .ins-plus { width: 44px; height: 44px; } }
  .ins-dot { width: 8px; height: 8px; border-radius: 50%; background: #10130a; animation: pulse 1s ease-in-out infinite; }
  @keyframes pulse { 0%,100% { opacity: .35 } 50% { opacity: 1 } }
`;

const ebtn = {
  fontSize: 12.5,
  padding: "7px 12px",
  borderRadius: 999,
  border: "1px solid var(--line)",
  background: "var(--surface)",
} as const;

const ebtnRow = {
  ...ebtn,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
} as const;

const nStyle = {
  fontFamily: "var(--font-display)",
  fontSize: "clamp(36px,6vw,64px)",
  lineHeight: ".8",
  color: "transparent",
  WebkitTextStroke: "1.4px var(--line-2)",
} as const;

/** Inline editor panel opened under a step's edit-row (text / cautions / table / speak-fix). */
const editorPanelStyle = {
  marginTop: 12,
  padding: "14px 16px",
  border: "1px dashed var(--line-2)",
  borderRadius: 12,
  background: "var(--bg2, color-mix(in srgb, var(--ink) 3%, transparent))",
  clear: "both",
} as const;

/** Dashed drop-zone-style button shown in place of the Frame when a step has no image yet. */
const picAddStyle = {
  width: 140,
  aspectRatio: "3/4",
  float: "left",
  margin: "0 18px 8px 0",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  border: "1.5px dashed var(--line-2)",
  borderRadius: 10,
  background: "transparent",
  color: "var(--muted)",
  fontSize: 12.5,
  fontFamily: "inherit",
  cursor: "pointer",
} as const;
